import { Users, UserCheck, UserMinus, BrainCircuit, Trophy, AlertTriangle, Activity, Sparkles } from "lucide-react";
import DiscRadar from "./DiscRadar";
import { DISC_COLOURS, formatScore, fmtDate } from "../EmploymentIntelligence";

export default function EIOverview({ overview, onOpenProfile, onSwitchTab }) {
  if (!overview) {
    return <div className="ei-empty">Loading dashboard…</div>;
  }
  if (overview.total_profiles === 0) {
    return (
      <div className="ei-empty">
        <span className="ei-empty-icon"><BrainCircuit size={28} /></span>
        <h3>No assessments yet</h3>
        <p>
          Upload your first DISC PDF to start building the workforce intelligence database. The
          system gets smarter with every assessment you add.
        </p>
        <button className="ei-secondary" style={{ marginTop: 18 }} onClick={() => onSwitchTab("upload")}>
          Upload your first assessment
        </button>
      </div>
    );
  }

  const dist = overview.style_distribution || {};
  const total = (dist.D || 0) + (dist.I || 0) + (dist.S || 0) + (dist.C || 0) || 1;
  const avg = overview.company_average || {};

  return (
    <>
      <div className="ei-section-head">
        <div>
          <h2>Executive overview</h2>
          <p>The vital signs of the NicheCom workforce. Hover any chart for detail.</p>
        </div>
      </div>

      <div className="ei-kpis">
        <KPI label="Total Profiles" value={overview.total_profiles} Icon={Users} accent="#0ea5e9" />
        <KPI label="Active Employees" value={overview.active_employees} Icon={UserCheck} accent="#10b981" />
        <KPI label="Former Employees" value={overview.former_employees} Icon={UserMinus} accent="#e11d48" />
        <KPI
          label="Most Common Style"
          value={overview.most_common_style || "—"}
          Icon={Sparkles}
          accent="#1a472a"
        />
        <KPI
          label="Retention Health"
          value={
            overview.total_profiles > 0
              ? `${Math.round((overview.active_employees / overview.total_profiles) * 100)}%`
              : "—"
          }
          Icon={Activity}
          accent="#f59e0b"
        />
      </div>

      <div className="ei-grid-2">
        {/* Company average radar */}
        <section className="ei-card">
          <div className="ei-card-head">
            <h3 className="ei-card-title"><Trophy size={15} /> Company average DISC</h3>
            <span className="ei-card-sub">Based on the latest assessment per employee</span>
          </div>
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
            <div style={{ width: "min(360px, 100%)" }}>
              <DiscRadar scores={avg} />
            </div>
          </div>
          <div className="ei-score-grid" style={{ marginTop: 12 }}>
            {["D", "I", "S", "C"].map((letter) => {
              const key = { D: "D", I: "I", S: "S", C: "C" }[letter];
              const num = avg[key];
              return (
                <div key={letter} className="ei-score-tile">
                  <div
                    className="ei-score-tile-letter"
                    style={{ background: DISC_COLOURS[letter].fill }}
                  >
                    {letter}
                  </div>
                  <div className="ei-score-tile-num">{formatScore(num)}</div>
                  <div className="ei-score-tile-label">{DISC_COLOURS[letter].label}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Style distribution bars */}
        <section className="ei-card">
          <div className="ei-card-head">
            <h3 className="ei-card-title"><BrainCircuit size={15} /> Primary style distribution</h3>
            <span className="ei-card-sub">Across all active and former profiles</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "6px 0 4px" }}>
            {["D", "I", "S", "C"].map((letter) => {
              const count = dist[letter] || 0;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={letter}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: "#0d1b2a",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span className={`ei-disc-chip ei-disc-chip-${letter}`}>{letter}</span>
                      {DISC_COLOURS[letter].label}
                    </span>
                    <span style={{ color: "#6a707d", fontWeight: 600 }}>
                      {count} {count === 1 ? "person" : "people"} · {pct}%
                    </span>
                  </div>
                  <div
                    style={{
                      background: "#f1f2ec",
                      borderRadius: 999,
                      overflow: "hidden",
                      height: 10,
                    }}
                  >
                    <div
                      style={{
                        background: DISC_COLOURS[letter].fill,
                        width: `${pct}%`,
                        height: "100%",
                        transition: "width 420ms cubic-bezier(0.22, 1, 0.36, 1)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="ei-grid-2" style={{ marginTop: 18 }}>
        {/* Department comparison */}
        <section className="ei-card">
          <div className="ei-card-head">
            <h3 className="ei-card-title"><Trophy size={15} /> Departments</h3>
            <span className="ei-card-sub">Headcount · average D / I / S / C</span>
          </div>
          {overview.by_department.length === 0 ? (
            <div style={{ padding: "20px 0", color: "#6a707d", fontSize: 13 }}>
              Add department names to your profiles to see department-level analytics here.
            </div>
          ) : (
            <div className="ei-dept-list">
              {overview.by_department.map((d) => (
                <div key={d.department} className="ei-dept-row">
                  <div>
                    <strong>{d.department}</strong>
                    <div className="ei-dept-row-meta">{d.count} {d.count === 1 ? "person" : "people"}</div>
                  </div>
                  <div>
                    {["D", "I", "S", "C"].map((letter) => {
                      const v = (d.average || {})[letter];
                      return (
                        <span
                          key={letter}
                          style={{
                            display: "inline-block",
                            fontSize: 11,
                            fontWeight: 700,
                            color: DISC_COLOURS[letter].fill,
                            marginRight: 6,
                          }}
                        >
                          {letter} {formatScore(v)}
                        </span>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 22 }}>
                    {["D", "I", "S", "C"].map((letter) => {
                      const v = (d.average || {})[letter] || 0;
                      return (
                        <div
                          key={letter}
                          style={{
                            width: 8,
                            background: DISC_COLOURS[letter].fill,
                            height: `${Math.max(2, (v / 100) * 22)}px`,
                            borderRadius: 2,
                            opacity: 0.85,
                          }}
                          title={`${letter}: ${formatScore(v)}`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recently added */}
        <section className="ei-card">
          <div className="ei-card-head">
            <h3 className="ei-card-title"><Activity size={15} /> Recently added</h3>
            <span className="ei-card-sub">Latest profiles to land in the database</span>
          </div>
          {overview.recent.length === 0 ? (
            <div style={{ padding: "20px 0", color: "#6a707d", fontSize: 13 }}>No recent activity.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {overview.recent.map((p) => (
                <button
                  key={p.id}
                  onClick={() => onOpenProfile(p.id)}
                  className="ei-secondary"
                  style={{ justifyContent: "space-between", width: "100%" }}
                >
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                    <strong style={{ fontWeight: 700 }}>{p.full_name}</strong>
                    <span style={{ color: "#6a707d", fontWeight: 500 }}>
                      {p.job_title || "—"} · {p.department || "—"}
                    </span>
                  </span>
                  <span style={{ color: "#6a707d", fontWeight: 500 }}>{fmtDate(p.updated_at)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function KPI({ label, value, Icon, accent }) {
  return (
    <div className="ei-kpi" style={{ "--accent": accent }}>
      <span
        style={{
          width: 28,
          height: 28,
          background: `${accent}1f`,
          color: accent,
          borderRadius: 8,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 2,
        }}
      >
        <Icon size={15} />
      </span>
      <span className="ei-kpi-label">{label}</span>
      <span className="ei-kpi-value">{value}</span>
    </div>
  );
}
